import React, { useState, useEffect } from 'react';
import {
  Settings, Bell, Shield, AlertTriangle, ClipboardCheck,
  Microscope, Loader2, Save, CheckCircle, Mail, Smartphone
} from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const notificationTypes = [
  {
    key: 'safety_event',
    label: 'Safety Events',
    description: 'Patient falls, medication errors, near misses',
    icon: AlertTriangle,
    color: 'bg-red-100 text-red-600',
  },
  {
    key: 'audit',
    label: 'Audits & Inspections',
    description: 'Scheduled audits and completed rounds',
    icon: ClipboardCheck,
    color: 'bg-blue-100 text-blue-600',
  },
  {
    key: 'risk',
    label: 'Risk Register',
    description: 'New risks and mitigation updates',
    icon: Shield,
    color: 'bg-orange-100 text-orange-600',
  },
  {
    key: 'infection',
    label: 'Infection Control',
    description: 'HAI reports and outbreak alerts',
    icon: Microscope,
    color: 'bg-purple-100 text-purple-600',
  },
  {
    key: 'system',
    label: 'System & Platform',
    description: 'Platform updates and maintenance',
    icon: Settings,
    color: 'bg-gray-100 text-gray-600',
  },
];

const channels = [
  { key: 'bell', label: 'In-App', icon: Bell, description: 'Notification bell' },
  { key: 'email', label: 'Email', icon: Mail, description: 'Email alerts' },
  { key: 'push', label: 'Push', icon: Smartphone, description: 'Browser push' },
];

const HospitalSettingsPage = () => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get('/api/users/me/notification-preferences')
      .then(setPrefs)
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = (type, channel) => {
    setPrefs(prev => ({
      ...prev,
      [type]: { ...prev[type], [channel]: !prev[type][channel] }
    }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await api.put('/api/users/me/notification-preferences', prefs);
      setPrefs(updated);
      setDirty(false);
      toast.success('Notification preferences saved');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const enableAllChannel = (channel) => {
    setPrefs(prev => {
      const next = { ...prev };
      notificationTypes.forEach(t => { next[t.key] = { ...next[t.key], [channel]: true }; });
      return next;
    });
    setDirty(true);
  };

  const disableAllChannel = (channel) => {
    setPrefs(prev => {
      const next = { ...prev };
      notificationTypes.forEach(t => { next[t.key] = { ...next[t.key], [channel]: false }; });
      return next;
    });
    setDirty(true);
  };

  const enableAll = () => {
    const next = {};
    notificationTypes.forEach(t => { next[t.key] = { bell: true, email: true, push: true }; });
    setPrefs(next);
    setDirty(true);
  };

  const disableAll = () => {
    const next = {};
    notificationTypes.forEach(t => { next[t.key] = { bell: false, email: false, push: false }; });
    setPrefs(next);
    setDirty(true);
  };

  const countEnabled = (channel) => {
    if (!prefs) return 0;
    return notificationTypes.filter(t => prefs[t.key]?.[channel]).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="settings-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--niko-teal)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hospital-settings-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-500 text-sm mt-1">Manage notification preferences and delivery channels</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={!dirty || saving}
          className="btn-primary gap-2"
          data-testid="save-preferences-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </Button>
      </div>

      {/* Notification Preferences Matrix */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
              <Bell className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Notification Preferences</h2>
              <p className="text-sm text-gray-500">
                Choose which notifications you receive and how they're delivered.
              </p>
            </div>
          </div>
        </div>

        {/* Channel Headers */}
        <div className="grid grid-cols-[1fr_auto] items-center px-5 py-3 bg-gray-50 border-b border-gray-100">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Notification Type</span>
          <div className="flex items-center gap-2">
            {channels.map(ch => (
              <div key={ch.key} className="w-[72px] flex flex-col items-center gap-0.5">
                <ch.icon className="w-4 h-4 text-gray-500" />
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{ch.label}</span>
                <span className="text-[9px] text-gray-400">{countEnabled(ch.key)}/{notificationTypes.length}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Preference Rows */}
        <div className="divide-y divide-gray-100">
          {notificationTypes.map((type) => {
            const Icon = type.icon;
            const typePrefs = prefs?.[type.key] || {};
            const anyEnabled = Object.values(typePrefs).some(Boolean);
            return (
              <div
                key={type.key}
                className={`grid grid-cols-[1fr_auto] items-center px-5 py-4 transition-colors ${
                  anyEnabled ? '' : 'bg-gray-50/50'
                }`}
                data-testid={`notification-pref-${type.key}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${type.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className={`font-medium text-sm ${anyEnabled ? 'text-gray-900' : 'text-gray-500'}`}>
                      {type.label}
                    </p>
                    <p className="text-xs text-gray-500">{type.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {channels.map(ch => (
                    <div key={ch.key} className="w-[72px] flex justify-center">
                      <Switch
                        checked={typePrefs[ch.key] ?? false}
                        onCheckedChange={() => handleToggle(type.key, ch.key)}
                        data-testid={`toggle-${type.key}-${ch.key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center gap-4 flex-wrap">
          <button onClick={enableAll} className="text-xs text-[var(--niko-teal)] font-medium hover:underline" data-testid="enable-all-btn">
            Enable All
          </button>
          <span className="text-gray-300">|</span>
          <button onClick={disableAll} className="text-xs text-gray-500 font-medium hover:underline" data-testid="disable-all-btn">
            Disable All
          </button>
          <span className="text-gray-300">|</span>
          {channels.map(ch => (
            <span key={ch.key} className="flex items-center gap-1">
              <button onClick={() => enableAllChannel(ch.key)} className="text-xs text-[var(--niko-teal)] font-medium hover:underline" data-testid={`enable-all-${ch.key}`}>
                All {ch.label}
              </button>
              <span className="text-gray-300">/</span>
              <button onClick={() => disableAllChannel(ch.key)} className="text-xs text-gray-400 font-medium hover:underline" data-testid={`disable-all-${ch.key}`}>
                None
              </button>
              {ch.key !== channels[channels.length - 1].key && <span className="text-gray-300 ml-1">|</span>}
            </span>
          ))}
        </div>
      </div>

      {/* Channel Info Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        {channels.map(ch => {
          const enabled = countEnabled(ch.key);
          const isActive = ch.key === 'bell';
          return (
            <div
              key={ch.key}
              className={`rounded-xl border p-4 ${
                isActive ? 'bg-[var(--niko-teal)]/5 border-[var(--niko-teal)]/20' : 'bg-white border-gray-100'
              }`}
              data-testid={`channel-info-${ch.key}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  isActive ? 'bg-[var(--niko-teal)]/10' : 'bg-gray-100'
                }`}>
                  <ch.icon className={`w-4 h-4 ${isActive ? 'text-[var(--niko-teal)]' : 'text-gray-500'}`} />
                </div>
                <div>
                  <p className="font-medium text-sm text-gray-900">{ch.label}</p>
                  {isActive && <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-medium">Active</span>}
                  {!isActive && <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium">Coming Soon</span>}
                </div>
              </div>
              <p className="text-xs text-gray-500">
                {ch.key === 'bell' && 'Notifications appear in your dashboard bell icon in real-time.'}
                {ch.key === 'email' && 'Critical alerts sent to your registered email address.'}
                {ch.key === 'push' && 'Browser push notifications for urgent items.'}
              </p>
              <p className="text-xs text-gray-400 mt-2">{enabled} of {notificationTypes.length} types enabled</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HospitalSettingsPage;
